import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMerge,
  faRemoveFormat,
  faTableColumns,
  faTableRows,
  faTrash,
} from "@nnr/fontawesome-sharp-light/icons/sharp/light";
import { useCurrentEditor } from "@tiptap/react";
import { AnimatePresence, motion } from "framer-motion";
import { useWindowSize } from "usehooks-ts";

import { AlignDropdownMenu } from "@/components/tiptap-ui/align-dropdown-menu/align-dropdown-menu";
import { FontDropdownMenu } from "@/components/tiptap-ui/font-dropdown-menu/font-dropdown-menu";
import { HeadingDropdownMenu } from "@/components/tiptap-ui/heading-dropdown-menu/heading-dropdown-menu";
import { HighlightPopover } from "@/components/tiptap-ui/highlight-popover/highlight-popover";
import InsertDropdownMenu from "@/components/tiptap-ui/insert-dropdown-menu/insert-dropdown-menu";
import { LinkPopover } from "@/components/tiptap-ui/link-popover/link-popover";
import { ListDropdownMenu } from "@/components/tiptap-ui/list-dropdown-menu/list-dropdown-menu";
import { MarkButton } from "@/components/tiptap-ui/mark-button/mark-button";
import { Separator } from "@/components/tiptap-ui/separator/separator";
import { TextAlignButton } from "@/components/tiptap-ui/text-align-button/text-align-button";
import { UndoRedoButton } from "@/components/tiptap-ui/undo-redo-button/undo-redo-button";
import { UnifiedButton } from "@/components/tiptap-ui/unified-button";
import { cn } from "@/lib/utils";
import { PageBody } from "../app-layout";
import { Row } from "../ui/flex";

const MenuButton = ({
  icon,
  onClick,
  activeState,
  tooltip,
}: {
  icon: IconProp;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  activeState?: () => "on" | "off";
  tooltip?: string;
}) => {
  return (
    <UnifiedButton
      aria-label={tooltip}
      tooltip={tooltip}
      data-style="ghost"
      data-active-state={activeState?.()}
      onClick={onClick}
    >
      <FontAwesomeIcon icon={icon} />
    </UnifiedButton>
  );
};

export const WriterToolbar = ({
  className,
  children,
}: {
  className?: string;
  children?: React.ReactNode;
}) => {
  const { width } = useWindowSize();
  const { editor } = useCurrentEditor();
  const hasTableTools =
    (editor?.isActive("table") || editor?.isActive("tableCell")) ?? false;

  if (!editor) {
    return null;
  }

  return (
    <>
      <PageBody rigid document outerClassName="pt-2 pb-0">
        <Row className={cn("flex-wrap gap-0.5 pb-2", className)} align="center">
          <UndoRedoButton action="undo" />
          <UndoRedoButton action="redo" />

          <Separator />

          <MarkButton type="bold" />
          <MarkButton type="italic" />
          <MarkButton type="strike" />
          <MarkButton type="code" />
          <MarkButton type="underline" />
          <MarkButton type="superscript" />
          <MarkButton type="subscript" />
          <HighlightPopover />
          <LinkPopover />

          <Separator />

          <HeadingDropdownMenu />
          <FontDropdownMenu
            fonts={["Normal", "Cursive", "Monospace", "Serif"]}
          />

          <Separator />

          <ListDropdownMenu types={["bulletList", "orderedList", "taskList"]} />

          <Separator />

          {width < 1024 ? (
            <AlignDropdownMenu />
          ) : (
            <>
              <TextAlignButton align="left" />
              <TextAlignButton align="center" />
              <TextAlignButton align="right" />
              <TextAlignButton align="justify" />
            </>
          )}

          <Separator />

          <InsertDropdownMenu />

          <Separator />

          <MenuButton
            tooltip="Clear formatting"
            icon={faRemoveFormat}
            onClick={() =>
              editor.chain().focus().clearNodes().unsetAllMarks().run()
            }
            activeState={() => "off"}
          />

          {children && (
            <>
              <Separator />
              {children}
            </>
          )}
        </Row>
      </PageBody>
      <AnimatePresence>
        {hasTableTools && (
          <motion.div
            initial={{ height: 0 }}
            animate={{ height: 40 }}
            exit={{ height: 0 }}
            transition={{ duration: 0.23, ease: "easeInOut" }}
            className="bg-background-2 flex flex-row items-center justify-start gap-2 overflow-hidden px-4 text-sm"
          >
            <UnifiedButton
              aria-label="Insert Table"
              tooltip="Insert Table"
              data-style="ghost"
              data-active-state="off"
              style={{ fontSize: "12px" }}
              onClick={() => editor.chain().focus().addColumnAfter().run()}
            >
              <FontAwesomeIcon icon={faTableColumns} />
              <span className="tiptap-button-text">Add Column</span>
            </UnifiedButton>
            <UnifiedButton
              aria-label="Insert Table"
              tooltip="Insert Table"
              data-style="ghost"
              data-active-state="off"
              style={{ fontSize: "12px" }}
              onClick={() => editor.chain().focus().addRowAfter().run()}
            >
              <FontAwesomeIcon icon={faTableRows} />
              <span className="tiptap-button-text">Add Row</span>
            </UnifiedButton>
            <Separator />
            <UnifiedButton
              aria-label="Insert Table"
              tooltip="Insert Table"
              data-style="ghost"
              data-active-state="off"
              style={{ fontSize: "12px" }}
              onClick={() => editor.chain().focus().deleteColumn().run()}
            >
              <FontAwesomeIcon icon={faTableColumns} />
              <span className="tiptap-button-text">Delete Column</span>
            </UnifiedButton>
            <UnifiedButton
              aria-label="Insert Table"
              tooltip="Insert Table"
              data-style="ghost"
              data-active-state="off"
              style={{ fontSize: "12px" }}
              onClick={() => editor.chain().focus().deleteRow().run()}
            >
              <FontAwesomeIcon icon={faTableRows} />
              <span className="tiptap-button-text">Delete Row</span>
            </UnifiedButton>
            <Separator />
            <UnifiedButton
              aria-label="Insert Table"
              tooltip="Insert Table"
              data-style="ghost"
              data-active-state="off"
              style={{ fontSize: "12px" }}
              onClick={() => editor.chain().focus().mergeOrSplit().run()}
            >
              <FontAwesomeIcon icon={faMerge} />
              <span className="tiptap-button-text">Merge/Split</span>
            </UnifiedButton>
            <Separator />
            <UnifiedButton
              aria-label="Insert Table"
              tooltip="Insert Table"
              data-style="ghost"
              data-active-state="off"
              style={{ fontSize: "12px" }}
              onClick={() => editor.chain().focus().deleteTable().run()}
            >
              <FontAwesomeIcon icon={faTrash} />
              <span className="tiptap-button-text">Delete Table</span>
            </UnifiedButton>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
